import React, { useState, useEffect, useCallback } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../auth/AuthContext';
import { track } from '../analytics';
import './BugReportModal.css';

const APP_VERSION = '1.0.0';

function BugReportModal({ open, onClose, currentView }) {
  const { user } = useAuth();
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  // Reset state when opening
  useEffect(() => {
    if (open) {
      setMessage('');
      setEmail(user?.email || '');
      setDone(false);
      setError('');
      setSubmitting(false);
    }
  }, [open, user]);

  const close = useCallback(() => {
    if (submitting) return;
    onClose();
  }, [submitting, onClose]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const trimmed = message.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        message: trimmed.slice(0, 4000),
        email: (email || '').trim().slice(0, 200),
        uid: user?.uid || '',
        page: currentView || '',
        userAgent: (navigator.userAgent || '').slice(0, 500),
        url: (window.location.href || '').slice(0, 500),
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        appVersion: APP_VERSION,
        createdAt: serverTimestamp(),
      };
      await addDoc(collection(db, 'bug_reports'), payload);
      track('bug_report_submitted', { page: currentView || '', has_email: !!payload.email });
      setDone(true);
    } catch (err) {
      console.error('Bug report failed:', err);
      setError("Couldn't send the report. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="bug-modal-backdrop" onClick={close}>
      <div className="bug-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="bug-modal-title">
        <button className="bug-modal-close" onClick={close} aria-label="Close">×</button>

        {!done ? (
          <>
            <div className="bug-modal-head">
              <div className="bug-modal-kicker">Feedback</div>
              <h2 id="bug-modal-title" className="bug-modal-title">Report a bug</h2>
              <p className="bug-modal-sub">
                Found something broken or confusing? Tell me what happened — I read every report.
              </p>
            </div>

            <form className="bug-modal-form" onSubmit={submit}>
              <label className="bug-modal-label">
                <span>What happened?</span>
                <textarea
                  className="bug-modal-textarea"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="e.g. Mega Feraligatr's type didn't change to Water/Dragon when I hit the mega button in Battle Advisor..."
                  rows={6}
                  maxLength={4000}
                  autoFocus
                />
                <span className="bug-modal-counter">{message.length} / 4000</span>
              </label>

              <label className="bug-modal-label">
                <span>Email <em>(optional — only if you want a reply)</em></span>
                <input
                  type="email"
                  className="bug-modal-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  maxLength={200}
                />
              </label>

              <div className="bug-modal-meta">
                Auto-attached: page <code>{currentView || '—'}</code>, browser, screen size{user ? ', your user id' : ''}.
              </div>

              {error && <div className="bug-modal-error">{error}</div>}

              <div className="bug-modal-actions">
                <button type="button" className="bug-modal-btn ghost" onClick={close} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="bug-modal-btn primary" disabled={!message.trim() || submitting}>
                  {submitting ? 'Sending…' : 'Send report'}
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="bug-modal-done">
            <div className="bug-modal-done-icon">✓</div>
            <h2 className="bug-modal-title">Thanks — got it.</h2>
            <p className="bug-modal-sub">Report received. I'll dig in as soon as I can.</p>
            <button className="bug-modal-btn primary" onClick={close}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default BugReportModal;
